import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Dumbbell, CalendarCheck, Flame, TrendingUp, Trophy, Activity, Loader2 } from "lucide-react";
import api from "@/lib/api";

export default function Share() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: false });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/share/${token}`);
        if (alive) setState({ loading: false, data, error: false });
      } catch {
        if (alive) setState({ loading: false, data: null, error: true });
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const { loading, data, error } = state;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50 px-4 py-10 dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-950">
      <div className="mx-auto max-w-lg">
        <div className="mb-6 flex items-center justify-center gap-2">
          <span className="grid h-9 w-9 place-items-center rounded-xl bg-emerald-500 text-white"><Activity className="h-5 w-5" /></span>
          <span className="font-display text-2xl font-bold tracking-tight">FitBersama</span>
        </div>

        {loading ? (
          <div className="flex justify-center py-20" data-testid="share-loading"><Loader2 className="h-8 w-8 animate-spin text-emerald-500" /></div>
        ) : error || !data ? (
          <div className="rounded-2xl border bg-card p-8 text-center" data-testid="share-error">
            <p className="text-lg font-semibold">Tautan tidak ditemukan</p>
            <p className="mt-1 text-sm text-muted-foreground">Link mungkin sudah dicabut atau tidak valid.</p>
            <Link to="/" className="mt-4 inline-block rounded-full bg-emerald-500 px-5 py-2 text-sm font-semibold text-white">Buka FitBersama</Link>
          </div>
        ) : (
          <div className="rounded-3xl border bg-card p-6 shadow-xl shadow-emerald-500/5 slide-up" data-testid="share-card">
            <div className="text-center">
              <div className="text-xs font-semibold uppercase tracking-wide text-emerald-500">Ringkasan Mingguan</div>
              <h1 className="mt-1 font-display text-3xl font-bold">{data.name}</h1>
              <p className="mt-1 text-sm text-muted-foreground">Progres 7 hari terakhir 🔥</p>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <Stat icon={Dumbbell} label="Sesi Latihan" value={data.sessions ?? 0} />
              <Stat icon={CalendarCheck} label="Check-in" value={data.checkins ?? 0} />
              <Stat icon={Flame} label="Kepatuhan" value={`${data.adherence ?? 0}%`} />
              <Stat icon={Activity} label="Total Volume" value={`${(data.volume ?? 0).toLocaleString()} kg`} />
              <Stat icon={TrendingUp} label="Perubahan Berat"
                value={data.weight_change != null ? `${data.weight_change > 0 ? "+" : ""}${data.weight_change} kg` : "—"} />
              <Stat icon={Trophy} label="Rekor Pribadi" value={data.prs?.length ?? 0} />
            </div>

            {data.prs?.length > 0 && (
              <div className="mt-4 rounded-xl bg-emerald-500/10 p-3">
                <div className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">🏆 PR Minggu Ini</div>
                <div className="mt-1 text-sm">{data.prs.join(", ")}</div>
              </div>
            )}

            <div className="mt-6 rounded-2xl bg-emerald-500 p-4 text-center text-white">
              <p className="text-sm font-medium">Mau lacak progresmu juga?</p>
              <Link to="/" className="mt-2 inline-block rounded-full bg-white px-5 py-2 text-sm font-bold text-emerald-600" data-testid="share-cta">
                Coba FitBersama Gratis
              </Link>
            </div>
            <p className="mt-4 text-center text-[11px] text-muted-foreground">Tampilan read-only · Dibagikan lewat FitBersama</p>
          </div>
        )}
      </div>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value }) => (
  <div className="rounded-2xl border bg-background p-4">
    <Icon className="h-4 w-4 text-emerald-500" />
    <div className="mt-2 font-display text-2xl font-bold tabular-nums">{value}</div>
    <div className="text-xs text-muted-foreground">{label}</div>
  </div>
);
